import { gql } from "@apollo/client"
import { ReimbursementBudgetFragment } from "~/shared/fragments/ReimbursementBudgetFragment"
import { RemoteWorkCostsFragment } from "~/shared/fragments/RemoteWorkCostsFragment"
import { VariableCompensationFragment } from "~/shared/fragments/VariableCompensationFragment"

const EMPLOYER_COST_FRAGMENT = gql`
  fragment EmployerCost on Employment {
    localEmployerCostMonthlyAmount
    localEmployerCostMonthlyCurrencyCode
    localEmployerCostOneoffAmount
    localEmployerCostOneoffCurrencyCode
    employerTaxesAndContributionsAmount
    employerTaxesAndContributionsCurrencyCode
  }
`

const REMOTE_WORK_FRAGMENT = gql`
  fragment RemoteWork on Employment {
    providedEquipmentList
    providedEquipmentOther
    providedEquipmentPolicy
    providedEquipmentPolicyBudget {
      currencyCode
      formatted
      subunit
      subunitToUnit
    }
    reimbursementPolicy {
      amount {
        currencyCode
        formatted
        subunit
        subunitToUnit
      }
      policy
    }
  }
`

const EMPLOYMENT_BENEFITS_FRAGMENT = gql`
  fragment EmploymentBenefits on Employment {
    suggestedBenefitPlans {
      id
      descriptiveName
      name
      provider {
        name
      }
      priceEstimate {
        currencies {
          currencyCode
          subunit
          subunitToUnit
        }
      }
    }
    benefitsDescription
    benefitsProvisionedEnrollments {
      collection {
        plan {
          id
          name
          priceEstimate {
            currencies {
              currencyCode
              formatted
              subunit
              subunitToUnit
            }
          }
        }
      }
    }
  }
`

export const ENGAGEMENT_FRAGMENT = gql`
  fragment StaffEmploymentPageFinanceEngagementFragment on Engagement {
    isPayrollTeamMember
    payrollCurrencyCode
    allowedToSubmitExpenses
    engagementType
    paymentsEngagementSettings {
      payoutsDisabled
      forcedProvider
    }
    company {
      id
      depositInvoiceRequired
      contractorsInvoicesGenerationDay
      invoicingSettings {
        monthsOfDeposit
      }
    }
    benefitsEnrollments {
      collection {
        id
        plan {
          name
          priceEstimate {
            currencies {
              currencyCode
              subunit
              subunitToUnit
            }
          }
        }
      }
    }
    contract {
      id
      salary
      salaryCurrencyCode
      rate
      rateCurrencyCode
      rateUnit
      state
      billingType
      paygBillingType
      contractorInvoicingSettings {
        id
        invoicingEnabled
      }
      expenseLimit
      expenseLimitCurrencyCode
      firstPayment
      firstPaymentCurrencyCode
    }
    pendingEngagementChange {
      id
      state
      hasAmendmentFlowAutomatable
      availableActions
    }
    employment {
      ...EmployerCost
      ...RemoteWork
      ...EmploymentBenefits
      ...RemoteWorkCostsFragment
      employmentType
      annualBonusType
      commissionPlanOffered
      countryCode
      isPayrollTeamMember
      commissionPlan {
        id
        filename
      }
      company {
        id
        countrySales {
          mightSellInChina
          mightSellInColombia
          mightSellInIsrael
          mightSellInBolivia
        }
        activeOrPendingScaleSubscription {
          id
          numberOfSeats
          availableSeatsCount
          pricePerSeat {
            subunit
            currencyCode
          }
          startsOn
        }
      }
      invoicingEmployeeSettings {
        id
        depositInvoiceRequired
        includeOnInvoices
        monthsOfDeposit
        auditHistory(only: ["include_on_invoices"]) {
          when
          by {
            id
            name
          }
          changes {
            to
          }
        }
      }
      employerOfRecord {
        cutOffMonthDay
        payrollCycleType
      }
      equityOffered
      id
      startDate
      endDate
      cutoffMonthDay
      lockedCurrencyCode
      monthlyCommission
      is13thSalaryEnabled
      is14thSalaryEnabled
      monthsFor13thSalary
      monthsFor14thSalary
      paymentCadence
      paymentDay
      publicHolidayDays
      paidHolidayDays
      salaryAmount
      hourlyRate
      salaryCurrencyCode
      latestSalaryCurrencyConversion {
        id
        exchangeRateValue
        expired
        receivedCurrencyCode
        receivedAmount
      }
      signOnBonusAmount
      state
      visa {
        acquisitionPayer
        acquisionFee
        acquisionFeeCurrencyCode
        expirationDate
        document {
          title
          description
          id
          filename
        }
        documentUrl
        required
        rightToWorkType
      }
      reimbursementBudgets {
        nodes {
          ...ReimbursementBudgetFragment
        }
      }
      workFocusedWithinCountryOfResidence
      sickPay {
        customDurationUnit
        customDurationValue
        id
        option
      }
      fullTimeHoursPerWeek
      partTimeDaysPerWeek
      partTimeHoursPerDay
    }
    id
    variableCompensations {
      nodes {
        ...VariableCompensationFragment
      }
    }
    state
    startDate
    cutoffMonthDay
    engagementType
  }

  ${EMPLOYER_COST_FRAGMENT}
  ${REMOTE_WORK_FRAGMENT}
  ${EMPLOYMENT_BENEFITS_FRAGMENT}
  ${VariableCompensationFragment}
  ${ReimbursementBudgetFragment}
  ${RemoteWorkCostsFragment}
`
